import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, FileText, Filter, Printer, Search } from "lucide-react";

import ResourcesDrawer from "../../components/payroll/ResourcesDrawer";
import DatePicker from "../../components/DatePicker";
import type { ArchivedForm } from './types';

import {
  BG,
  CARD,
  DARK,
  INK,
  LINE_STRONG,
  TEAL,
  TEAL_INK,
  ghostBtn,
} from "./styles";
import { describeForm } from "./constants";
import { useArchivedForms } from "./useArchivedForms";
import FormCard from "./components/FormCard";

const DatePickerAny = DatePicker as any;
const ResourcesDrawerAny = ResourcesDrawer as any;

export default function ArchivedForms() {
  const navigate = useNavigate();
  const { data: forms = [], isLoading: loading, error } = useArchivedForms();
  const [search, setSearch] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [resourcesOpen, setResourcesOpen] = useState(false);

  const errMsg = error instanceof Error ? error.message : null;
  const errorText = errMsg ? "Could not load archived forms: " + errMsg : "";

  const filtered = useMemo(() => {
         return forms.filter((f: ArchivedForm) => {
      if (search) {
        const q = search.toLowerCase();
        const desc = describeForm(f.form_type).toLowerCase();
        const hay = [
          f.form_type || "",
          desc,
          f.period_label || "",
          f.country || "",
        ]
          .join(" ")
          .toLowerCase();
        if (!hay.includes(q)) return false;
      }
      if (startDate && (f.period_end || "") < startDate) return false;
      if (endDate && (f.period_start || "") > endDate) return false;
      return true;
    });
  }, [forms, search, startDate, endDate]);

  return (
    <div
      style={{
        background: BG,
        minHeight: "100vh",
        fontFamily: "Inter, 'Plus Jakarta Sans', sans-serif",
        color: INK,
        fontSize: 14,
        lineHeight: 1.5,
        fontWeight: 500,
      }}
    >
      <div style={{ maxWidth: 1400, margin: "0 auto", padding: "24px 24px 60px" }}>
        <a
          onClick={() => navigate("/payroll/taxes")}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            color: TEAL_INK,
            fontWeight: 700,
            marginBottom: 14,
            cursor: "pointer",
            textDecoration: "none",
          }}
        >
          <ChevronLeft size={16} strokeWidth={2.5} />
          Back to Payroll taxes
        </a>

        <h1
          style={{
            fontSize: 24,
            fontWeight: 700,
            margin: "0 0 20px",
            color: INK,
          }}
        >
          Archived forms
        </h1>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 14,
            marginBottom: 20,
            flexWrap: "wrap",
          }}
        >
          <div style={{ position: "relative" }}>
            <span style={{ position: "absolute", left: 12, top: 11, color: DARK }}>
              <Search size={16} strokeWidth={2.5} />
            </span>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search"
              style={{
                padding: "9px 12px 9px 36px",
                width: 240,
                border: "1px solid " + LINE_STRONG,
                borderRadius: 10,
                fontSize: 14,
                fontFamily: "inherit",
                background: "white",
                color: INK,
                fontWeight: 600,
                outline: "none",
              }}
              onFocus={(e) => (e.currentTarget.style.borderColor = TEAL)}
              onBlur={(e) => (e.currentTarget.style.borderColor = LINE_STRONG)}
            />
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <label style={{ fontSize: 12, color: DARK, fontWeight: 700 }}>
              Start date
            </label>
            <DatePickerAny value={startDate} onChange={setStartDate} />
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <label style={{ fontSize: 12, color: DARK, fontWeight: 700 }}>
              End date
            </label>
            <DatePickerAny value={endDate} onChange={setEndDate} />
          </div>

          <button
            onClick={() => {
              setSearch("");
              setStartDate("");
              setEndDate("");
            }}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 7,
              padding: "9px 14px",
              borderRadius: 8,
              height: 40,
              background: "white",
              border: "1px solid " + LINE_STRONG,
              color: INK,
              fontSize: 13,
              fontWeight: 700,
              cursor: "pointer",
              fontFamily: "inherit",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.background = "#F9FAFB")}
            onMouseLeave={(e) => (e.currentTarget.style.background = "white")}
          >
            <Filter size={14} strokeWidth={2.5} />
            Clear filters
          </button>

          <div style={{ flex: 1 }} />

          <button
            onClick={() => window.print()}
            style={ghostBtn}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "#EEF1F4";
              e.currentTarget.style.color = INK;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "transparent";
              e.currentTarget.style.color = DARK;
            }}
          >
            <Printer size={14} strokeWidth={2.5} />
            Print
          </button>

          <button
            onClick={() => setResourcesOpen(true)}
            style={ghostBtn}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "#EEF1F4";
              e.currentTarget.style.color = INK;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "transparent";
              e.currentTarget.style.color = DARK;
            }}
          >
            <FileText size={14} strokeWidth={2.5} />
            Resources
          </button>
        </div>

        <div
          style={{
            color: DARK,
            margin: "14px 0",
            fontSize: 13,
            fontWeight: 600,
          }}
        >
          {loading
            ? "Loading..."
            : filtered.length +
              " form" +
              (filtered.length === 1 ? "" : "s") +
              " found"}
        </div>

        {errorText && (
          <div
            style={{
              background: "#FEF2F2",
              border: "1px solid #FCA5A5",
              borderRadius: 8,
              padding: "10px 12px",
              marginBottom: 14,
              color: "#991B1B",
              fontSize: 13,
              fontWeight: 600,
            }}
          >
            {errorText}
          </div>
        )}

        {!loading && filtered.length === 0 && !errorText && (
          <div
            style={{
              background: CARD,
              border: "1px dashed " + LINE_STRONG,
              borderRadius: 12,
              padding: "40px 20px",
              textAlign: "center",
              color: DARK,
              fontWeight: 600,
            }}
          >
            {forms.length === 0
              ? "No archived forms yet. Archive a PD7A from the Payroll taxes page."
              : "No forms match your filters."}
          </div>
        )}

        {filtered.map((f: ArchivedForm) => (
          <FormCard
            key={String(f.id)}
            form={f}
            onView={() => navigate("/payroll/taxes/archived/" + f.id)}
          />
        ))}
      </div>

      <ResourcesDrawerAny
        open={resourcesOpen}
        onClose={() => setResourcesOpen(false)}
        onNavigate={(path: string) => navigate(path)}
        country="CA"
      />
    </div>
  );
}